import { useState } from "react";
import { ChevronDown, MailOpen, PartyPopper } from "lucide-react";

import { ProgressReportView } from "@/components/progress-report-view";
import { formatDayMonth, formatFullDate } from "@/lib/relative-date";

/**
 * The learner's side of a progress report — and it is never called one.
 *
 * "Ataskaita" is what a school sends home about a child. The same text arriving as
 * a **letter** („laiškas") is addressed *to* them, which is what it actually is:
 * their name at the top, what they managed, one thing to try. So the child's word
 * for it is a letter everywhere, and the grown-up's screen keeps saying report,
 * because for them that is the honest word.
 *
 * The waiting state is built the same way round. It is not a progress bar towards
 * an unlock; it is a letter on its way — which is the only framing under which
 * "not yet" is something to look forward to.
 */

type Report = {
  id: string;
  audience: string;
  subjectName?: string | null;
  sections: Record<string, unknown>;
  stats: Record<string, any>;
  sessionCount: number;
  coveredFrom?: string | null;
  coveredThrough?: string | null;
  createdAt: string;
};

type Availability = {
  allowed: boolean;
  blockedBy?: string | null;
  subjectName?: string | null;
  sessionsDone: number;
  sessionsNeeded: number;
  daysLeft?: number;
  availableAt?: string | null;
  lastReportAt?: string | null;
};

const WEEK = 7;

/** Whole days the letter looks back over. */
function windowDays(report: Report): number | null {
  if (!report.coveredFrom) return null;

  const from = new Date(report.coveredFrom).getTime();
  const through = new Date(report.coveredThrough ?? report.createdAt).getTime();
  return Math.max(1, Math.round((through - from) / 86_400_000));
}

/**
 * The numbers, as pills a child reads at a glance. Same snapshot the grown-up's
 * panel uses — emoji instead of icons, because this row is meant to feel like a
 * scoreboard rather than a summary table.
 */
function LetterPills({ report }: { report: Report }) {
  const totals = report.stats?.totals ?? {};
  const points = report.stats?.points ?? {};

  const pills = [
    { emoji: "📅", value: report.sessionCount, label: "sesijos" },
    { emoji: "✅", value: totals.correct, label: "teisingai" },
    { emoji: "🎯", value: totals.accuracy != null ? `${totals.accuracy}%` : null, label: "tikslumas" },
    { emoji: "⏱️", value: totals.minutes != null ? `${totals.minutes} min.` : null, label: "laiko" },
    { emoji: "💎", value: points.window, label: "taškų" },
  ].filter((pill) => pill.value != null);

  if (pills.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {pills.map((pill) => (
        <div
          key={pill.label}
          className="inline-flex items-center gap-2 rounded-full border bg-background/70 px-3.5 py-2 shadow-sm backdrop-blur-sm"
        >
          <span className="text-base leading-none">{pill.emoji}</span>
          <span className="text-base font-bold leading-none">{pill.value}</span>
          <span className="text-xs text-muted-foreground">{pill.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * One letter, opened or closed.
 *
 * Every letter is collapsible and older ones start closed, because a letter is kept
 * rather than consumed: five of them stacked open is a wall, and the one a child
 * came back for is somewhere inside it. Closed, each is a single line with its own
 * title — which is exactly what the title is for.
 *
 * The newest one opens on arrival (there is nothing else the child came for) but
 * closes like the rest, so the page can be folded down to a list of what they have
 * been sent.
 */
export function ProgressLetterCard({
  report,
  headline,
  fresh = false,
  defaultOpen = false,
}: {
  report: Report;
  headline?: string;
  fresh?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const days = windowDays(report);

  // The AI's title when there is one. It is asked for and validated but not
  // required, so the fallback has to stand on its own — and it is the letter's own
  // date, which is the one thing guaranteed to differ between two letters. „Tavo
  // naujas laiškas" on every card would be exactly the sameness the title exists
  // to prevent.
  const written =
    typeof report.sections?.title === "string" ? report.sections.title.trim() : "";
  const title = written || headline || `Laiškas iš ${formatDayMonth(report.createdAt)}`;

  // The full date stays in the subtitle so "which one was that" is answerable —
  // unless the heading already is the date, in which case printing it twice just
  // reads as a mistake.
  const subtitle = [
    written || headline ? formatFullDate(report.createdAt) : null,
    days ? `${days} d. kelias` : "Visas tavo kelias iki šiol",
    report.subjectName,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border ${
        fresh ? "border-primary/40 shadow-lg" : ""
      }`}
    >
      {/* The paper: warm, and warmer still when the letter is the new one. */}
      <div
        className={`absolute inset-0 ${
          fresh
            ? "bg-gradient-to-br from-amber-500/15 via-pink-500/10 to-purple-500/15"
            : "bg-gradient-to-br from-muted/40 to-muted/10"
        }`}
      />
      {open && (
        <div className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-gradient-to-br from-amber-400/20 to-pink-400/20 blur-3xl" />
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="relative flex w-full items-center gap-3 p-4 text-left sm:px-6"
      >
        <span className="shrink-0 text-2xl">{open ? "💌" : "✉️"}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-bold leading-tight">{title}</span>
          <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
        </span>
        {/* Shown open as well as closed: the headline is the letter's own title now,
            so nothing else marks which one is the new one. */}
        {fresh && (
          <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary sm:inline-flex">
            <MailOpen className="h-3.5 w-3.5" />
            Naujas
          </span>
        )}
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="relative space-y-5 px-5 pb-6 sm:px-7">
          <LetterPills report={report} />
          <ProgressReportView report={report} />
        </div>
      )}
    </div>
  );
}

/**
 * The letter that has not arrived yet — one line, no bars.
 *
 * Two progress bars made waiting look like a task with sub-tasks, which is the
 * opposite of the point: a child cannot do anything about the week, and the three
 * sessions are not a checklist, they are just how far the next letter still has to
 * travel. One sentence with a real day in it („ir naujas laiškas atkeliaus
 * pirmadienį") answers the question the bars were gesturing at, and answers it
 * better, because a date is something you can wait for.
 *
 * Everything a child reads uses the one metaphor: a letter is on its way, it
 * arrives, they open it. Nothing is ever „rašoma" — the arrival copy is what they
 * meet first, and a second metaphor for the same state reads as a second thing
 * happening.
 */
export function ProgressLetterMeter({ availability }: { availability: Availability }) {
  if (availability.allowed) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-emerald-500/40 p-5 text-center">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/15 via-amber-500/10 to-pink-500/15" />
        <div className="relative space-y-1">
          <p className="text-4xl">💌</p>
          <p className="text-lg font-bold">Hurá! Tau atėjo naujas laiškas</p>
          <p className="text-sm text-muted-foreground">
            Atidaryk ir pažiūrėk, kuo gali didžiuotis
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2.5 rounded-2xl border bg-card/50 px-4 py-3">
      <span className="shrink-0 text-lg">✉️</span>
      <p className="text-sm text-muted-foreground">{nextLetterSentence(availability)}</p>
    </div>
  );
}

/**
 * What is left before the next letter, as one sentence.
 *
 * Both conditions are folded into it rather than listed, because the letter arrives
 * at whichever comes later and a child does not need that spelled out as two rows.
 *
 * Exported because the profile card says the same thing in the same words: a child
 * who is told „dar 3 sesijos" in one place and something else in another is being
 * told about two different letters.
 */
export function nextLetterSentence(availability: Availability): string {
  const needed = availability.sessionsNeeded ?? 0;
  const left = Math.max(needed - (availability.sessionsDone ?? 0), 0);
  const when = whenPhrase(availability);
  const sessions = `${left} ${left === 1 ? "tos pačios temos sesija" : "tos pačios temos sesijos"}`;

  if (left > 0 && when) return `Dar ${sessions} — ir naujas laiškas atkeliaus ${when}`;
  if (left > 0) return `Dar ${sessions} — ir gausi naują laišką`;
  if (when) return `Naujas laiškas atkeliaus ${when}`;

  return "Naujas laiškas jau pakeliui";
}

// Accusative, which is how a Lithuanian says "on Monday".
const WEEKDAYS = [
  "sekmadienį",
  "pirmadienį",
  "antradienį",
  "trečiadienį",
  "ketvirtadienį",
  "penktadienį",
  "šeštadienį",
];

/**
 * When the week is up, in words a child can hold onto. A named day beats "in 5
 * days" — you can wait for Monday.
 *
 * A full week away is „po savaitės" rather than a weekday, because the cooldown is
 * exactly seven days: naming the day would name *today's* day and read as "today".
 */
function whenPhrase(availability: Availability): string | null {
  const days = availability.daysLeft ?? 0;
  if (days <= 0) return null;
  if (days === 1) return "rytoj";
  if (days >= WEEK) return "po savaitės";

  const at = availability.availableAt ? new Date(availability.availableAt) : null;
  if (at && !Number.isNaN(at.getTime())) return WEEKDAYS[at.getDay()];

  return `po ${days} d.`;
}

/** The "hooray, a letter" strip for a page that is not this one. */
export function LetterTeaser({ availability }: { availability?: Availability | null }) {
  if (availability?.allowed) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
        <PartyPopper className="h-3.5 w-3.5" />
        Naujas laiškas!
      </span>
    );
  }

  return <span className="text-sm text-muted-foreground">Atidaryti &rarr;</span>;
}
